/**
****************************************************************************************
Project: Readabl
File: navigationOptions.js
Description: This file contains all components imports and menu options used across the project.
� Healthcare Triangle, Inc
****************************************************************************************
**/

import React, { useEffect, useState, lazy } from "react";
import * as c from "./constants";
const Dashboard = lazy(() => import('../../pages/Dashboard/Dashboard'));
const DocManagement = lazy(() => import('../../pages/DocumentManagement/DocManagement'));
const UserManagement = lazy(() => import('../../pages/UserManagement/UserManagement'));
const ManualReview = lazy(() => import('../../pages/ManualReview/ManualReview'));
const AuditLog = lazy(() => import('../../pages/AuditLog/AuditLog'));
const AuditReport = lazy(() => import('../../pages/AuditReport/AuditReport'));
const SuperAdmin = lazy(() => import('../../pages/SuperAdmin/SuperAdmin'));
const UserProfile = lazy(() => import('../../pages/UserProfile/UserProfile'));
const InfoScreen = lazy(() => import('../../pages/InfoScreen/InfoScreen'));
const CustomUsageReport = lazy(() => import('../../pages/CustomUsageReport/CustomUsageReport'));
const MonthlyReport = lazy(() => import('../../pages/MonthlyReport/MonthlyReport'));
const DocInfo = lazy(() => import('../../pages/SuperAdmin/DocInfo/DocInfo'));
const ManualProcess = lazy(() => import('../../pages/SuperAdmin/ManualProcess/ManualProcess'));
const GoogleNlpDashboard = lazy(() => import('../../pages/MedicalEntity/MedicalEntity'));
const Configuration = lazy(() => import('../../pages/Configuration/Configuration'));
const ConfusionMatrix = lazy(() => import('../../pages/SuperAdmin/ConfusionMatrix/ConfusionMatrix'));
const Accuracy = lazy(() => import('../../pages/SuperAdmin/AccuracyTracker/Accuracy'));
const SubCategory = lazy(() => import('../../pages/SuperAdmin/SubCategoryTable/SubCategoryTable'));
const SpeechRecognition = lazy(() => import('../../Experiments/SpeechRecognition/SpeechRecognition'));
const Patient = lazy(() => import('../../pages/PatientList/PatientList'));
const VirtualAssistant = lazy(() => import("../../pages/VirtualAssistant/VirtualAssistant"));
const SSORedirect = lazy(() => import("../../pages/SSORedirect/SSORedirect"));
const PerformanceReport = lazy(() => import("../../pages/UserPerformance/UserPerformance"));
const PatientDetails = lazy(() => import('../../pages/PatientDetails/PatientDetails'));
const SomethingWentWrong = lazy(() => import('../../components/SomethingWentWrong/SomethingWentWrong'));
const FailedStatusReport = lazy(() => import('../../pages/FailedStatusReport/FailedStatusReport'));
const DocumentDetail = lazy(() => import('../../pages/DocumentDetail/DocumentDetail'));
const CategoryBasedExtraction = lazy(() => import('../../Experiments/CategoryBasedExtraction/CategoryBasedExtraction'));
const CategoryTraining = lazy(() => import('../../pages/CategoryTraining/CategoryTraining'));
const DischargeSummary = lazy(() => import('../../Experiments/DischargeSummary/DischargeSummary'));

// menu options list 
export const MENU_OPTIONS = [
  {
    "value": c.PAGE_URLS[c.PAGE_INFO],
    "role": [],
    "component": InfoScreen,
    hideNavigationMenu: true,
  },
  {
    "value": c.PAGE_URLS[c.SSO_REDIRECT],
    "role": [],
    "component": SSORedirect,
  },
  {
    "value": c.PAGE_URLS[c.PAGE_READABL_DOWN],
    "role": ["Staff"],
    "component": SomethingWentWrong,
    hideNavigationMenu: true, 
  },
  {
    "value": c.PAGE_URLS[c.PAGE_USER_PROFILE],
    "role": [],
    "component": UserProfile,
  },
  {
    "value": c.PAGE_URLS[c.SPEECH_RECOGNITION],
    "role": [],
    "component": SpeechRecognition,
  },
  {
    "value": c.PAGE_URLS[c.PAGE_GOOGLE_NLP],
    "role": [],
    "component": GoogleNlpDashboard,
  },
  {
    "label": "Home",
    "value": c.PAGE_URLS[c.PAGE_DASHBOARD],
    "role": ["Staff", "Admin"],
    "component": Dashboard,
    showMenu: true
  },
  {
    "label": "Data",
    "value": c.PAGE_URLS[c.PAGE_DATA],
    "children": [
      {
        "label": "Doc Management",
        "value": c.PAGE_URLS[c.PAGE_DOC_MGMT],
        "role": ["Staff", "Admin"],
        "component": DocManagement,
        showMenu: true,
        hasOwnPath: true,
        "children": [
        //   {
        //   "label": "Manual Review",
        //   "value": c.PAGE_URLS[c.PAGE_MANUAL_REVIEW],
        //   "role": ["Staff", "Admin"],
        //   "component": ManualReview,
        // },
        {
          "label": "Document Detail",
          "value": c.PAGE_URLS[c.PAGE_MANUAL_REVIEW],
          "role": ["Staff", "Admin"],
          "component": DocumentDetail,
        }
      ]
      },
      {
        "label": "Patients",
        "value": c.PAGE_URLS[c.PAGE_PATIENT],
        "role": ["Admin"],
        "component": Patient,
        showMenu: true,
        hasOwnPath: true,
        "children": [
          {
          "label": "Patient Details",
          "value": c.PAGE_URLS[c.PAGE_PATIENT_DETAILS],
          "role": ["Admin"],
          "component": PatientDetails,
          },
          {
          "label": "Discharge Summary",
          "value": c.PAGE_URLS[c.PAGE_DISCHARGE_SUMMARY],
          "role": ["Admin"],
          "component": DischargeSummary
        }
        ]
      },
      {
        "label": "Virtual Assisstant",
        "value": c.PAGE_URLS[c.PAGE_VIRTUAL_ASSISTANT],
        "role": ["Staff", "Admin"],
        "component": VirtualAssistant,
        showMenu: true
      },
      {
        "label": "Speech Recognition",
        "value": c.PAGE_URLS[c.SPEECH_RECOGNITION],
        "role": ["Staff", "Admin"],
        "component": SpeechRecognition,
        showMenu: true
      }
    ]
  },
  {
    "label": "Admin",
    "value": c.PAGE_URLS[c.PAGE_ADMIN],
    "children": [
      {
        "label": "User Management",
        "value": c.PAGE_URLS[c.PAGE_USER_MGMT],
        "role": ["Admin"],
        "component": UserManagement,
        showMenu: true
      },
      {
        "label": "Manual Process",
        "value": c.PAGE_URLS[c.PAGE_DOC_STUCK],
        "role": ["Admin"],
        "component": ManualProcess,
        showMenu: true
      },
      {
        "label": "Manual Training",
        "value": c.PAGE_URLS[c.PAGE_MANUAL_TRAINING],
        "children": [
          {
            "label": "Sub Category Training",
            "value": c.PAGE_URLS[c.PAGE_SUBCATEGORY_TRAINING],
            "role": ["Admin"],
            "component": CategoryTraining,
            showMenu: true
          },
          {
            "label": "Category Based Extraction",
            "value": c.PAGE_URLS[c.PAGE_CATEGORY_EXTRACTION],
            "role": ["Admin"],
            "component": CategoryBasedExtraction,
            showMenu: true
          }
        ]
      },
      {
        "label": "Configuration",
        "value": c.PAGE_URLS[c.PAGE_CONFIGURATION],
        "role": ["Admin"],
        "component": Configuration,
        showMenu: true
      },
    ]
  },
  {
    "label": "Reports",
    "value": c.PAGE_URLS[c.PAGE_REPORTS],
    "children": [
      {
        "label": "Usage Report",
        "value": c.PAGE_URLS[c.PAGE_USAGE_REPORT],
        "children": [
          {
            "label": "Monthly Report",
            "value": c.PAGE_URLS[c.PAGE_MONTHLY_USAGE_REPORT],
            "role": ["Admin"],
            "component": MonthlyReport,
            showMenu: true
          },
          {
            "label": "Custom Report",
            "value": c.PAGE_URLS[c.PAGE_CUSTOM_USAGE_REPORT],
            "role": ["Admin"],
            "component": CustomUsageReport,
            showMenu: true
          },
        ]
      },
      {
        "label": "Performance Report",
        "value": c.PAGE_URLS[c.PAGE_PERFORMANCE_REPORT],
        "role": ["Admin"],
        "component": PerformanceReport,
        showMenu: true
      },
      {
        "label": "Failed Status Report",
        "value": c.PAGE_URLS[c.PAGE_FAILED_STATUS_REPORT],
        "role": ["Admin"],
        "component": FailedStatusReport,
        showMenu: true
      },
      {
        "label": "Audit",
        "value": c.PAGE_URLS[c.PAGE_AUDIT],
        "children": [
          {
            "label": "Audit Log",
            "value": c.PAGE_URLS[c.PAGE_AUDIT_LOG],
            "role": ["Admin"],
            "component": AuditLog,
            showMenu: true
          },
          {
            "label": "Audit Report",
            "value": c.PAGE_URLS[c.PAGE_AUDIT_REPORT],
            "role": ["Admin"],
            "component": AuditReport,
            showMenu: true
          },
        ]
      },
    ]
  },
  {
    "label": "Super Admin",
    "value": c.PAGE_URLS[c.PAGE_SUPER_ADMIN],
    hasOwnPath: true,
    "component": SuperAdmin,
    "children": [
      {
        "label": "Manual Process",
        "value": c.PAGE_URLS[c.PAGE_DOC_STUCK],
        "role": ["Admin"],
        "component": ManualProcess,
      },
      {
        "label": "Accuracy Tracker",
        "value": c.PAGE_URLS[c.PAGE_ACCURACY],
        "role": ["Admin"],
        component: Accuracy
      },
      {
        "label": "Sub Category",
        "value": c.PAGE_URLS[c.PAGE_SUBCATEGORY],
        "role": ["Admin"],
        component: SubCategory
      },
      {
        "label": "Confusion Matrix",
        "value": c.PAGE_URLS[c.PAGE_CONFUSION],
        "role": ["Admin"],
        component: ConfusionMatrix
      },
      {
        "label": "Doc Info",
        "value": c.PAGE_URLS[c.PAGE_DOC_INFO],
        "role": ["Admin"],
        component: DocInfo
      },
    ]
  },
]

window.MENU_OPTIONS = MENU_OPTIONS;
